// Painel "Telefonia ao vivo" — filas de telefone do home care (Reducel), pensado para TV:
// fundo escuro, números grandes, uma faixa de indicadores e um cartão por fila (posição fixa). Fila com
// cliente esperando brilha em vermelho; o alerta também vem escrito (não só na cor).
// Lê o retrato do servidor a cada 5 s (o servidor mantém uma conexão só com o Reducel).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase';

const LIMITE_MIN = 5;
// Filas fixas do painel, nesta ordem (pedido do Alexandre, 18/10) — aparecem sempre; as outras filas
// do home vêm depois, só quando têm movimento. Ouvidoria = só a Ouvidoria Grupo (21/10).
const PRINCIPAIS = ['Homebaby_Intercorrencias', 'Solicitacoes_Homebaby', 'Escalista', 'Admissao_Homebaby', 'NUCLEO_ADMINISTRATIVO', 'Ouvidoria_Grupo_PB'];
const LIMITE = LIMITE_MIN * 60;
const C = {
  fundo: '#0a1020', cartao: '#111a2e', borda: '#1f2b46', texto: '#e8eefc', fraco: '#8a9bbd', apagado: '#4b5a78',
  vermelho: '#f43f5e', ambar: '#f59e0b', verde: '#22c55e', azul: '#60a5fa', roxo: '#a78bfa',
};
const hms = s => { s = Math.max(0, Math.round(s || 0)); const h = Math.floor(s / 3600); return `${h ? `${h}:` : ''}${String(Math.floor((s % 3600) / 60)).padStart(h ? 2 : 1, '0')}:${String(s % 60).padStart(2, '0')}`; };

const CSS = `
@keyframes tel-alerta { 0%,100% { box-shadow: 0 0 0 1px ${C.vermelho}, 0 0 18px -6px ${C.vermelho}; } 50% { box-shadow: 0 0 0 2px ${C.vermelho}, 0 0 34px -2px ${C.vermelho}; } }
@keyframes tel-ponto { 0%,100% { opacity: 1; } 50% { opacity: .25; } }
.tel-alerta { animation: tel-alerta 1.4s ease-in-out infinite; }
.tel-ponto { animation: tel-ponto 1.6s ease-in-out infinite; }
.tel-num { font-variant-numeric: tabular-nums; letter-spacing: -0.02em; }
@media (prefers-reduced-motion: reduce) { .tel-alerta, .tel-ponto { animation: none; } }
`;

/** Situação da fila: define cor, texto do selo e ordem no painel. */
function situacao(f) {
  if (f.emEspera > 0 && !f.atendentes) return { k: 0, nome: 'Esperando sem atendente', cor: C.vermelho };
  if (f.emEspera > 0 && f.maiorEspera >= LIMITE) return { k: 1, nome: `Espera > ${LIMITE_MIN} min`, cor: C.vermelho };
  if (f.emEspera > 0) return { k: 2, nome: `${f.emEspera} esperando`, cor: C.vermelho };
  if (f.emAtendimento > 0) return { k: 3, nome: 'Em atendimento', cor: C.ambar };
  if (f.atendentes > 0) return { k: 4, nome: 'Livre', cor: C.verde };
  return { k: 5, nome: 'Sem atendente', cor: C.apagado };
}

function Indicador({ titulo, valor, detalhe, cor, alerta, grande }) {
  return (
    <div className={alerta ? 'tel-alerta' : undefined} style={{ background: C.cartao, border: `1px solid ${alerta ? cor : C.borda}`, borderRadius: 18, padding: 'clamp(10px, 1.2vw, 18px) clamp(12px, 1.4vw, 20px)', position: 'relative', overflow: 'hidden', minWidth: 0 }}>
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 5, background: cor }} />
      <div style={{ color: C.fraco, fontSize: 'clamp(0.68rem, 0.85vw, 0.9rem)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{titulo}</div>
      <div className="tel-num" style={{ color: alerta ? cor : C.texto, fontSize: grande ? 'clamp(2rem, 3.6vw, 4rem)' : 'clamp(1.7rem, 3vw, 3.2rem)', fontWeight: 800, lineHeight: 1.1, marginTop: 4 }}>{valor}</div>
      {detalhe && <div style={{ color: C.fraco, fontSize: 'clamp(0.68rem, 0.85vw, 0.9rem)', marginTop: 2 }}>{detalhe}</div>}
    </div>
  );
}

function CartaoFila({ f, passou }) {
  const s = situacao(f);
  const esperando = f.emEspera > 0;
  const espera = f.maiorEspera + (esperando ? passou : 0);
  const encerradas = f.atendidas + f.abandonadas;
  const ns = encerradas ? Math.round((f.atendidas / encerradas) * 100) : null;
  const corNs = ns == null ? C.apagado : ns >= 90 ? C.verde : ns >= 75 ? C.ambar : C.vermelho;
  const ocioso = s.k === 5 && !f.total;
  return (
    <div className={esperando ? 'tel-alerta' : undefined} style={{ background: C.cartao, border: `1px solid ${esperando ? C.vermelho : C.borda}`, borderRadius: 18, padding: 'clamp(10px, 1.1vw, 16px)', display: 'flex', flexDirection: 'column', gap: 10, opacity: ocioso ? 0.55 : 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
        <div style={{ color: C.texto, fontWeight: 800, fontSize: 'clamp(0.9rem, 1.15vw, 1.25rem)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={f.fila}>{f.nome}</div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: `${s.cor}22`, color: s.cor, border: `1px solid ${s.cor}55`, borderRadius: 999, padding: '2px 10px', fontSize: 'clamp(0.66rem, 0.8vw, 0.85rem)', fontWeight: 800, whiteSpace: 'nowrap' }}>
          <span className={esperando ? 'tel-ponto' : undefined} style={{ width: 8, height: 8, borderRadius: 99, background: s.cor }} />{s.nome}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <div style={{ color: C.fraco, fontSize: 'clamp(0.62rem, 0.75vw, 0.8rem)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>Em espera</div>
          <div className="tel-num" style={{ color: esperando ? C.vermelho : C.apagado, fontSize: 'clamp(1.8rem, 3vw, 3.2rem)', fontWeight: 800, lineHeight: 1 }}>{f.emEspera}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: C.fraco, fontSize: 'clamp(0.62rem, 0.75vw, 0.8rem)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>Maior espera</div>
          <div className="tel-num" style={{ color: !esperando ? C.apagado : espera >= LIMITE ? C.vermelho : C.ambar, fontSize: 'clamp(1.4rem, 2.4vw, 2.6rem)', fontWeight: 800, lineHeight: 1.1 }}>{esperando ? hms(espera) : '—'}</div>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', color: C.fraco, fontSize: 'clamp(0.7rem, 0.85vw, 0.92rem)' }}>
        <span><b style={{ color: f.atendentes ? C.verde : C.apagado }}>{f.atendentes}</b> online</span>
        <span><b style={{ color: f.emAtendimento ? C.ambar : C.apagado }}>{f.emAtendimento}</b> em ligação</span>
        {f.emPausa > 0 && <span><b style={{ color: C.roxo }}>{f.emPausa}</b> em pausa</span>}
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', color: C.fraco, fontSize: 'clamp(0.66rem, 0.8vw, 0.86rem)', marginBottom: 4 }}>
          <span><b style={{ color: C.texto }}>{f.atendidas}</b> atendidas · <b style={{ color: f.abandonadas ? C.vermelho : C.texto }}>{f.abandonadas}</b> abandonadas</span>
          <span style={{ color: corNs, fontWeight: 800 }}>{ns == null ? '—' : `${ns}%`}</span>
        </div>
        <div style={{ height: 6, borderRadius: 99, background: '#1c2742', overflow: 'hidden' }} title="Nível de serviço do dia (atendidas ÷ atendidas + abandonadas)">
          <div style={{ width: `${ns ?? 0}%`, height: '100%', background: corNs, transition: 'width .6s' }} />
        </div>
      </div>
    </div>
  );
}

export default function PainelTelefonia() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [recebidoEm, setRecebidoEm] = useState(0);
  const [agora, setAgora] = useState(Date.now());
  const [soMovimento, setSoMovimento] = useState(() => { try { return localStorage.getItem('tel-so-movimento') !== 'nao'; } catch (_) { return true; } });
  const raiz = useRef(null);

  useEffect(() => {
    let vivo = true;
    const buscar = async () => {
      if (document.hidden) return;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const r = await fetch('/api/telefonia-aovivo', { headers: { Authorization: `Bearer ${session?.access_token || ''}` } });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `Erro ${r.status}`);
        if (vivo) { setDados(j); setRecebidoEm(Date.now()); setErro(''); }
      } catch (e) { if (vivo) setErro(e.message); }
    };
    buscar();
    const t = setInterval(buscar, 5000);
    const r = setInterval(() => setAgora(Date.now()), 1000);
    return () => { vivo = false; clearInterval(t); clearInterval(r); };
  }, []);
  useEffect(() => { try { localStorage.setItem('tel-so-movimento', soMovimento ? 'sim' : 'nao'); } catch (_) { /* sem armazenamento */ } }, [soMovimento]);

  const passou = dados ? Math.max(0, Math.round((agora - recebidoEm) / 1000)) : 0;
  const filas = useMemo(() => {
    const todas = dados?.filas || [];
    const pos = f => { const i = PRINCIPAIS.indexOf(f.fila); return i >= 0 ? i : PRINCIPAIS.length + todas.indexOf(f); };
    return todas
      .filter(f => PRINCIPAIS.includes(f.fila) || !soMovimento || f.atendentes || f.emEspera || f.total)
      .sort((a, b) => pos(a) - pos(b));
  }, [dados, soMovimento]);
  const t = dados?.topo || {};
  const corNs = (t.nivelServico ?? 100) >= 90 ? C.verde : (t.nivelServico ?? 100) >= 75 ? C.ambar : C.vermelho;
  const telaCheia = () => { const el = raiz.current; if (!document.fullscreenElement) el?.requestFullscreen?.(); else document.exitFullscreen?.(); };
  const relogio = new Date(agora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const dataHoje = new Date(agora).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });

  return (
    <div ref={raiz} style={{ minHeight: '100vh', background: `radial-gradient(1100px 520px at 0% -10%, #1d3a8a55, transparent), radial-gradient(900px 500px at 100% 110%, #0e749055, transparent), ${C.fundo}`, color: C.texto, padding: 'clamp(12px, 1.8vw, 28px)', fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif", boxSizing: 'border-box' }}>
      <style>{CSS}</style>

      <header style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14, marginBottom: 'clamp(12px, 1.6vw, 24px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className={dados?.conectado ? 'tel-ponto' : undefined} style={{ width: 12, height: 12, borderRadius: 99, background: erro ? C.vermelho : dados?.conectado ? C.verde : C.ambar }} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 'clamp(1.1rem, 1.6vw, 1.7rem)' }}>Telefonia · Home Care</div>
            <div style={{ color: C.fraco, fontSize: 'clamp(0.72rem, 0.85vw, 0.9rem)', textTransform: 'capitalize' }}>{dataHoje}</div>
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ color: erro ? C.vermelho : C.fraco, fontSize: '0.8rem' }}>
            {erro ? `Sem atualização: ${erro}` : dados ? `${dados.conectado ? 'ao vivo' : 'reconectando'} · atualizado há ${passou}s` : 'conectando ao Reducel…'}
          </span>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', color: C.fraco, fontSize: '0.8rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={soMovimento} onChange={e => setSoMovimento(e.target.checked)} /> outras filas só com movimento
          </label>
          <button type="button" onClick={telaCheia} style={{ border: `1px solid ${C.borda}`, background: C.cartao, color: C.texto, borderRadius: 10, padding: '6px 12px', cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem' }}>⛶ Tela cheia</button>
          <div className="tel-num" style={{ fontWeight: 800, fontSize: 'clamp(1.6rem, 2.6vw, 2.8rem)' }}>{relogio}</div>
        </div>
      </header>

      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 'clamp(8px, 1vw, 16px)' }}>
        <Indicador titulo="Em espera" valor={t.emEspera ?? '—'} detalhe={t.emEspera ? 'clientes aguardando agora' : 'ninguém aguardando'} cor={C.vermelho} alerta={t.emEspera > 0} grande />
        <Indicador titulo={`Espera > ${LIMITE_MIN} min`} valor={t.acimaLimite ?? '—'} detalhe="clientes" cor={C.ambar} alerta={t.acimaLimite > 0} />
        <Indicador titulo="Maior espera" valor={t.emEspera ? hms((t.maiorEspera || 0) + passou) : '—'} detalhe={`tempo médio ${hms(t.tempoMedioEspera)}`} cor={C.ambar} alerta={(t.maiorEspera || 0) >= LIMITE} />
        <Indicador titulo="Atendentes online" valor={t.atendentesOnline ?? '—'} detalhe={`${t.emAtendimento ?? 0} em ligação${t.emPausa ? ` · ${t.emPausa} em pausa` : ''}`} cor={C.verde} />
        <Indicador titulo="Finalizadas hoje" valor={t.finalizados ?? '—'} detalhe={`${t.receptivas ?? 0} receptivas · ${t.ativas ?? 0} ativas`} cor={C.azul} />
        <Indicador titulo="Nível de serviço" valor={`${t.nivelServico ?? '—'}%`} detalhe={`${t.abandonadas ?? 0} abandonadas hoje`} cor={corNs} alerta={(t.nivelServico ?? 100) < 75} />
      </section>

      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(240px, 19vw, 340px), 1fr))', gap: 'clamp(8px, 1vw, 16px)', marginTop: 'clamp(12px, 1.6vw, 24px)' }}>
        {filas.map(f => <CartaoFila key={f.fila} f={f} passou={passou} />)}
      </section>
      {dados && !filas.length && <div style={{ color: C.fraco, textAlign: 'center', padding: 40, fontWeight: 600 }}>Nenhuma fila do home com atendente logado ou ligação hoje.</div>}
      {dados && <div style={{ color: C.apagado, fontSize: '0.75rem', textAlign: 'right', marginTop: 12 }}>{filas.length} de {dados.filas.length} filas do home · as {PRINCIPAIS.length} principais sempre aparecem; as outras, quando têm movimento</div>}
    </div>
  );
}
